/**
 * **「选词造词」候选词选择器**（2026-11-04 用户要求）。
 *
 * ## 用户原话
 *
 * > "在这个字某个音的批量造词下面，再加个按钮，叫「选词造词」，就是你去查权威词库，
 * >  列出常用词语和成语，然后我点击选择我要的词语和成语，然后保存。"
 *
 * ## 数据从哪来
 *
 * 服务器的 `dictation_word_bank` 表（迁移 0022）：一次性导入的公开字典数据，
 * 只保留"每个字都在站内字表里"的词（约 19 万条）。**不联网**，查的是本地 SQL。
 *
 * ## 行为
 *
 * 1. 打开时按这个字拉候选（短词在前）；
 * 2. 勾选想要的词（已经在**这本书**里的会标灰、不可选）；
 * 3. 保存：逐条 `createWord()`（拼音自动算、类别用词库给的），
 *    再把这个字**钉在这个读音上**（与「批量造词」完全同一条路）。
 */
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Card';
import { toErrorMessage } from '../../lib/api/error';
import {
  createWord,
  listWordCandidates,
  setWordCharReadings,
  type WordCandidate,
} from '../../lib/api/dictationAdmin';
import type { HanziView } from '../../lib/api/dictation';
import { cn } from '../../lib/utils';

export interface WordCandidatePickerProps {
  open: boolean;
  /** 造的是哪个字的词（要它的课本/年级/学期/字面）。 */
  hanzi: HanziView | null;
  /** 这个字在这些词里读哪个音。 */
  reading: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function WordCandidatePicker({
  open,
  hanzi,
  reading,
  onClose,
  onSaved,
}: WordCandidatePickerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<WordCandidate[]>([]);
  /** 本页过滤后的条数（只用于提示；不再当"总数"—— 精确总数要扫全表，太慢）。 */
  const [total, setTotal] = useState(0);
  /** 下一批的起点（`null` = 没有了）。 */
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  /** 正在加载下一批（与首次加载分开，避免整块变成"查词库…"）。 */
  const [loadingMore, setLoadingMore] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [failures, setFailures] = useState<{ text: string; message: string }[]>([]);

  /** 每次打开：清空 + 拉**第一批**。 */
  useEffect(() => {
    if (!open || hanzi === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPicked(new Set());
    setFailures([]);
    setNextOffset(null);
    listWordCandidates(hanzi.id, { limit: 60, offset: 0, reading })
      .then((data) => {
        if (cancelled) return;
        setCandidates(data.candidates);
        setTotal(data.total);
        setNextOffset(data.nextOffset);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, hanzi, reading]);

  /**
   * **显示下一批**（2026-11-04 用户要求："一次不要全部显示出来，一批一批来"）。
   *
   * 按词频排序的下一页追加到列表末尾；已经勾选的不会丢（`picked` 按词面存）。
   */
  const loadMore = async () => {
    if (hanzi === null || nextOffset === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await listWordCandidates(hanzi.id, { limit: 60, offset: nextOffset, reading });
      setCandidates((prev) => [...prev, ...data.candidates]);
      setTotal(data.total);
      setNextOffset(data.nextOffset);
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * 服务端已经**把"这本书里已有"的词过滤掉了**（2026-11-04 用户要求：
   * "数据库里已经有某些词语的话，就不用显示这些词语了"），所以这里全是可选的。
   */
  const selectable = candidates;

  const toggle = (wordText: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(wordText)) next.delete(wordText);
      else next.add(wordText);
      return next;
    });
  };

  const save = async () => {
    if (hanzi === null || picked.size === 0 || saving) return;
    setSaving(true);
    setFailures([]);
    const failed: { text: string; message: string }[] = [];
    for (const wordText of picked) {
      const candidate = candidates.find((item) => item.wordText === wordText);
      try {
        const word = await createWord({
          textbookId: hanzi.textbookId,
          wordText,
          category: candidate?.category ?? 'phrase',
          /** 用**词级拼音**（多音字读对：校对 = jiào duì），没有就给空串让服务端推算。 */
          pinyin: candidate?.pinyin ?? '',
          fromTextbook: false,
          gradeCode: hanzi.gradeCode,
          semester: hanzi.semester,
        });
        const chars = [...word.wordText];
        if (chars.includes(hanzi.charText)) {
          await setWordCharReadings(
            word.id,
            chars.map((ch) => (ch === hanzi.charText ? reading : '')),
          );
        }
      } catch (err) {
        failed.push({ text: wordText, message: toErrorMessage(err) });
      }
    }
    setSaving(false);
    await onSaved();
    if (failed.length === 0) {
      onClose();
    } else {
      /** 有失败的：留在窗口里逐条报，并把已成功的从候选里剔掉（避免重复点）。 */
      setFailures(failed);
      setPicked(new Set());
      setCandidates((prev) => prev.filter((item) => failed.some((fail) => fail.text === item.wordText)));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={hanzi === null ? '选词造词' : `给「${hanzi.charText}」选词（读 ${reading}）`}
      size="lg"
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          从词库里挑现成的词，勾上就能加进来（拼音会自动算）。标灰的在这本书里已经有了。
        </p>

        {loading ? (
          <p className="py-6 text-center text-sm text-slate-500">正在查词库…</p>
        ) : error !== null ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            查词库失败：{error}
          </p>
        ) : candidates.length === 0 ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            词库里没有含「{hanzi?.charText ?? ''}」的词，用「批量造词」自己写吧。
          </p>
        ) : (
          /*
            2026-11-04 用户要求（第二轮）：
            · **不显示**"词语 / 成语"标签；
            · **拼音在词的上面**（与字卡网格同一种读法：先看读音，再看字面）；
            · **排版整齐** —— 用等高网格，不再用长短不一的流式 chip：
              手机上 2 列、宽屏 3 列，每格固定高度、拼音与词都居中。
          */
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {candidates.map((item) => {
              const checked = picked.has(item.wordText);
              return (
                <li key={item.wordText}>
                  <button
                    type="button"
                    disabled={saving}
                    aria-pressed={checked}
                    onClick={() => toggle(item.wordText)}
                    className={cn(
                      'flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-xl border px-2 py-2 transition-colors',
                      checked
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-slate-300 bg-white hover:bg-slate-50',
                    )}
                  >
                    {/* 拼音在上：算不出来时用「—」占位，保证每格高度一致。 */}
                    <span className="max-w-full truncate font-mono text-[11px] leading-4 text-slate-500">
                      {item.pinyin === '' ? '—' : item.pinyin}
                    </span>
                    <span
                      className={cn(
                        'text-base font-semibold leading-5',
                        checked ? 'text-brand-800' : 'text-slate-900',
                      )}
                    >
                      {item.wordText}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/*
          **显示下一批**（2026-11-04 用户要求："一次不要全部显示出来，一批一批来"）。
          只在还有下一页时出现；打开弹窗永远是先给一批（按词频从高到低）。
        */}
        {!loading && error === null && nextOffset !== null && (
          <div className="flex flex-col items-center gap-1 pt-1">
            <Button variant="secondary" onClick={() => void loadMore()} loading={loadingMore}>
              显示下一批
            </Button>
            <span className="text-xs text-slate-500">
              已显示 {candidates.length} 个（按词频从高到低）
            </span>
          </div>
        )}

        {failures.length > 0 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <p className="font-medium">有 {failures.length} 个没存上：</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              {failures.map((item) => (
                <li key={item.text}>
                  「{item.text}」：{item.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {picked.size > 0 && <Badge tone="neutral">已选 {picked.size} 个</Badge>}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button
            onClick={() => void save()}
            loading={saving}
            disabled={picked.size === 0 || selectable.length === 0}
          >
            保存已选
          </Button>
        </div>
      </div>
    </Modal>
  );
}
